import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { patientsGraphqlService } from '@/services/api/patients/patientsGraphqlService';

export interface PatientDetail {
  enrollment_patient_id?: number;
  emr_patient_clinic_data_id?: number;
  emr_patient_master_id?: number;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  dob?: string;
  gender?: string;
  mobile_number?: string;
  home_number?: string;
  work_phone?: string;
  email?: string;
  address_line_one?: string;
  address_line_two?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  emr_id?: string;
  emr_name?: string;
  clinic_id?: number;
  clinic_name?: string;
  clinic_facility_id?: number;
  clinic_facility_name?: string;
  provider_id?: number;
  provider_name?: string;
  patient_group_id?: number;
}

export const usePatientDetailsForGroups = (
  limit: number = 10, 
  offset: number = 0, 
  patientGroupId?: number
) => {
  const { hasuraToken } = useAuth();
  const { toast } = useToast();
  const [patients, setPatients] = useState<PatientDetail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchPatients = async (searchFilters?: any) => {
    if (!hasuraToken) return;

    setIsLoading(true);
    setError(null);

    try {
      // Filter by specific group ID if provided, otherwise get ungrouped patients
      const baseWhere = patientGroupId 
        ? { patient_group_id: { _eq: patientGroupId } }
        : { patient_group_id: { _is_null: true } };
      
      // Combine base filter with search filters properly
      const where = searchFilters && Object.keys(searchFilters).length > 0
        ? { _and: [baseWhere, searchFilters] }
        : baseWhere;

      console.log('Fetching patients with params:', { offset, limit, where });

      const response = await patientsGraphqlService.getPatientDetailsForGroup(
        hasuraToken,
        offset,
        limit,
        where
      );

      console.log('API Response:', response);

      const patientsData = (response as any)?.data?.pep_vw_patient_details || [];
      const totalCountData = (response as any)?.data?.pep_vw_patient_details_aggregate?.aggregate?.count || 0;
      console.log('Patients data:', patientsData.length, 'patients, total count:', totalCountData);
      
      setPatients(patientsData);
      setTotalCount(totalCountData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch patients';
      console.error('Error fetching patients:', err);
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
      setPatients([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [hasuraToken, limit, offset, patientGroupId]);

  return {
    patients,
    isLoading,
    error,
    totalCount,
    refetch: fetchPatients
  };
};
